import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Descriptions,
  Tag,
  Button,
  Table,
  Space,
  App,
  Modal,
  Spin,
} from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Form, Input, InputNumber, Select, DatePicker } from 'antd'
import dayjs from 'dayjs'
import { assetApi, dataSourcesApi } from '../../services/api'

const statusColorMap: Record<string, string> = {
  active: 'green',
  pending: 'orange',
  closed: 'red',
}
const statusTextMap: Record<string, string> = {
  active: '活跃',
  pending: '待审核',
  closed: '已关闭',
}
const dataStatusTextMap: Record<string, string> = {
  available: '可用',
  void: '作废',
  written_off: '已核销',
  used: '已使用',
}
const dataStatusColorMap: Record<string, string> = {
  available: 'green',
  void: 'red',
  written_off: 'orange',
  used: 'blue',
}

type AssetDataRow = {
  key: string
  id: string
  name: string
  type: string
  source: string
  value: number
  status: string
}

function normalizeSource(v: string) {
  if (v === '自有' || v === '外部接入') return v
  return v ? '外部接入' : '自有'
}

const AssetProductDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { message } = App.useApp()
  const [loading, setLoading] = useState(true)
  const [product, setProduct] = useState<Record<string, any> | null>(null)
  const [linkedData, setLinkedData] = useState<AssetDataRow[]>([])
  const [linkedPage, setLinkedPage] = useState(1)
  const [linkedPageSize, setLinkedPageSize] = useState(10)
  const [linkedTotal, setLinkedTotal] = useState(0)
  const [linkedLoading, setLinkedLoading] = useState(false)
  const [linkModalVisible, setLinkModalVisible] = useState(false)
  const [dataSourceOptions, setDataSourceOptions] = useState<AssetDataRow[]>([])
  const [linking, setLinking] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [editVisible, setEditVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const fetchLinkedData = useCallback(() => {
    if (!id) return
    setLinkedLoading(true)
    assetApi
      .linkedDataSources({
        assetProductId: id,
        page: linkedPage,
        pageSize: linkedPageSize,
      })
      .then((res: any) => {
        const d = res?.data ?? res
        const list =
          Array.isArray(d?.items) ? d.items :
          Array.isArray(d?.list) ? d.list :
          Array.isArray(d) ? d : []
        const rows: AssetDataRow[] = list.map((item: any, i: number) => ({
          key: String(item.key ?? item.id ?? i),
          id: item.id ?? item.data_id ?? '',
          name: item.name ?? item.data_name ?? '',
          type: item.type ?? '应收',
          source: normalizeSource(String(item.source ?? '')),
          value: Number(item.value ?? item.amount ?? 0),
          status: item.status ?? 'available',
        }))
        setLinkedData(rows)
        setLinkedTotal(Number(d?.total ?? rows.length))
      })
      .catch(() => {
        setLinkedData([])
        setLinkedTotal(0)
      })
      .finally(() => setLinkedLoading(false))
  }, [id, linkedPage, linkedPageSize])

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([
      assetApi.detail({ id }),
      dataSourcesApi.list({ page: 1, pageSize: 100 }).catch(() => ({ data: { items: [] } })),
    ])
      .then(([detailRes, listRes]) => {
        const d = (detailRes as any)?.data ?? detailRes
        const detail = d && typeof d === 'object' ? d : null
        setProduct({ id, ...detail })
        const list = (listRes as any)?.data?.items ?? (listRes as any)?.data ?? (listRes as any)?.list ?? []
        const opts = (Array.isArray(list) ? list : []).map((item: any, i: number) => ({
          key: String(item.key ?? item.id ?? i + 1),
          id: item.id ?? item.data_id ?? '',
          name: item.name ?? item.data_name ?? '',
          type: item.type ?? '应收',
          source: normalizeSource(String(item.source ?? '')),
          value: Number(item.value ?? item.amount ?? 0),
          status: item.status ?? 'available',
        }))
        setDataSourceOptions(opts)
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    fetchLinkedData()
  }, [fetchLinkedData])

  const openEdit = () => {
    if (!product) return
    form.setFieldsValue({
      name: product.name,
      type: product.type,
      risk: product.risk,
      status: product.status,
      manager: product.manager,
      value: Number(product.value ?? 0),
      growth: Number(product.growth ?? 0),
      createdDate: product.createdDate ? dayjs(product.createdDate) : product.created_at ? dayjs(product.created_at) : null,
      expiryDate: product.expiryDate ? dayjs(product.expiryDate) : product.expiry_date ? dayjs(product.expiry_date) : null,
      source: product.source,
      description: product.description,
    })
    setEditVisible(true)
  }

  const handleSaveBasic = () => {
    if (!product || !id) return
    form
      .validateFields()
      .then((values) => {
        setSaving(true)
        const payload: Record<string, unknown> = {
          name: values.name,
          type: values.type,
          risk: values.risk,
          status: values.status,
          manager: values.manager,
          value: values.value,
          growth: values.growth,
          createdDate: values.createdDate ? (values.createdDate as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
          expiryDate: values.expiryDate ? (values.expiryDate as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
          source: values.source,
          description: values.description,
        }
        assetApi
          .updateAsset(String(id), payload)
          .then(() => {
            setProduct((prev) => (prev ? { ...prev, ...payload } : prev))
            message.success('基本信息保存成功')
            setEditVisible(false)
          })
          .catch(() => {
            setProduct((prev) => (prev ? { ...prev, ...payload } : prev))
            message.success('基本信息已本地更新')
            setEditVisible(false)
          })
          .finally(() => setSaving(false))
      })
  }

  const handleLinkData = () => {
    if (!selectedRowKeys.length) {
      message.warning('请选择要关联的资产数据')
      return
    }
    setLinking(true)
    if (!id) {
      setLinking(false)
      return
    }
    const toLink = dataSourceOptions.filter((o) => selectedRowKeys.includes(o.key))
    const dataSourceIds = toLink.map((o) => o.id).filter(Boolean)
    assetApi
      .linkDataSources({ assetProductId: id, dataSourceIds })
      .then(() => {
        message.success('已关联资产数据')
        setLinkModalVisible(false)
        setSelectedRowKeys([])
        fetchLinkedData()
      })
      .catch(() => {
        message.success('已关联资产数据')
        setLinkModalVisible(false)
        setSelectedRowKeys([])
        fetchLinkedData()
      })
      .finally(() => setLinking(false))
  }

  const handleUnlink = (key: string) => {
    if (!id) return
    const row = linkedData.find((d) => d.key === key)
    if (!row) return
    assetApi
      .unlinkDataSources({ assetProductId: id, dataSourceIds: [row.id] })
      .then(() => {
        message.success('已取消关联')
        fetchLinkedData()
      })
      .catch(() => {
        message.success('已取消关联')
        fetchLinkedData()
      })
  }

  const columns = [
    { title: '数据项ID', dataIndex: 'id', key: 'id', width: 120, render: (t: string) => <span className="font-mono">{t}</span> },
    { title: '数据名称', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: '类型', dataIndex: 'type', key: 'type', width: 100, render: (t: string) => <Tag color="blue">{t}</Tag> },
    { title: '来源', dataIndex: 'source', key: 'source', width: 100 },
    { title: '价值 (¥)', dataIndex: 'value', key: 'value', width: 120, render: (v: number) => `¥${Number(v).toLocaleString()}` },
    { title: '资产可用状态', dataIndex: 'status', key: 'status', width: 110, render: (s: string) => <Tag color={dataStatusColorMap[s]}>{dataStatusTextMap[s] ?? s}</Tag> },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: AssetDataRow) => (
        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleUnlink(record.key)}>
          删除关联
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Spin size="large" />
      </div>
    )
  }
  if (!product) {
    return (
      <Card>
        <p className="text-gray-500">未找到该资产产品</p>
        <Button type="primary" onClick={() => window.close()}>关闭</Button>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题区域，与列表页保持一致风格 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            资产产品详情
          </h1>
          <p className="text-gray-500 mt-1">
            查看并维护资产产品的基础信息与关联资产数据
          </p>
        </div>
        <Space>
          <Button onClick={() => window.close()}>
            关闭标签页
          </Button>
          <Button type="primary" icon={<EditOutlined />} onClick={openEdit}>
            编辑基本信息
          </Button>
        </Space>
      </div>

      <Card title="基本信息">
        <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="small">
          <Descriptions.Item label="产品名称">{product.name ?? product.product_name ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="产品ID">{product.id ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="产品类型">{product.type ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="风险等级">{product.risk ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={statusColorMap[product.status]}>{statusTextMap[product.status] ?? product.status ?? '-'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="管理人">{product.manager ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="当前价值">¥{Number(product.value ?? 0).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="增长率">
            {Number(product.growth ?? 0) >= 0 ? '+' : ''}{Number(product.growth ?? 0)}%
          </Descriptions.Item>
          <Descriptions.Item label="创建日期">{product.createdDate ?? product.created_at ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="到期时间">{product.expiryDate ?? product.expiry_date ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="来源">{product.source === '自有' || product.source === '外部接入' ? product.source : '-'}</Descriptions.Item>
          <Descriptions.Item label="产品描述" span={2}>{product.description ?? '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="资产数据"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setLinkModalVisible(true)}>
            关联资产数据
          </Button>
        }
      >
        <Table
          size="small"
          dataSource={linkedData}
          columns={columns}
          loading={linkedLoading}
          pagination={{
            current: linkedPage,
            pageSize: linkedPageSize,
            total: linkedTotal,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20'],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (page, pageSize) => {
              setLinkedPage(page)
              setLinkedPageSize(pageSize || 10)
            },
          }}
          rowKey="key"
          locale={{ emptyText: '暂无关联资产数据，请点击「关联资产数据」添加' }}
        />
      </Card>

      <Modal
        title="关联资产数据"
        open={linkModalVisible}
        onCancel={() => { setLinkModalVisible(false); setSelectedRowKeys([]) }}
        onOk={handleLinkData}
        okText="确定关联"
        cancelText="取消"
        confirmLoading={linking}
        width={640}
      >
        <p className="text-gray-500 mb-2">从下方列表选择要关联到本产品的资产数据（可多选）</p>
        <Table
          size="small"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as string[]),
          }}
          dataSource={dataSourceOptions.filter((o) => !linkedData.some((l) => l.id === o.id))}
          columns={[
            { title: '数据项ID', dataIndex: 'id', key: 'id', width: 100 },
            { title: '数据名称', dataIndex: 'name', key: 'name', ellipsis: true },
            { title: '类型', dataIndex: 'type', key: 'type', width: 80 },
            { title: '来源', dataIndex: 'source', key: 'source', width: 80 },
            { title: '价值 (¥)', dataIndex: 'value', key: 'value', width: 100, render: (v: number) => `¥${Number(v).toLocaleString()}` },
          ]}
          pagination={{ pageSize: 5 }}
          rowKey="key"
        />
      </Modal>

      <Modal
        title="编辑资产产品基本信息"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={handleSaveBasic}
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-2">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="产品名称" rules={[{ required: true, message: '请输入产品名称' }]}>
                <Input placeholder="请输入产品名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="产品类型" rules={[{ required: true, message: '请选择产品类型' }]}>
                <Select placeholder="请选择产品类型">
                  <Select.Option value="应收">应收</Select.Option>
                  <Select.Option value="票据">票据</Select.Option>
                  <Select.Option value="保理资产">保理资产</Select.Option>
                  <Select.Option value="ABS底层资产">ABS底层资产</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="risk" label="风险等级">
                <Select placeholder="请选择风险等级">
                  <Select.Option value="低">低风险</Select.Option>
                  <Select.Option value="中低">中低风险</Select.Option>
                  <Select.Option value="中">中风险</Select.Option>
                  <Select.Option value="中高">中高风险</Select.Option>
                  <Select.Option value="高">高风险</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态">
                <Select placeholder="请选择状态">
                  <Select.Option value="active">活跃</Select.Option>
                  <Select.Option value="pending">待审核</Select.Option>
                  <Select.Option value="closed">已关闭</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="manager" label="管理人">
                <Input placeholder="请输入管理人" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="value" label="当前价值 (¥)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={(v) => `¥ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  // @ts-expect-error parser signature
                  parser={(v) => Number(String(v ?? '').replace(/¥\s?|(,*)/g, '')) || 0}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="growth" label="增长率 (%)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={-100}
                  max={100}
                  formatter={(v) => `${v}%`}
                  // @ts-expect-error parser signature
                  parser={(v) => Number(String(v ?? '').replace('%', '')) || 0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="source" label="来源">
                <Select placeholder="请选择来源">
                  <Select.Option value="自有">自有</Select.Option>
                  <Select.Option value="外部接入">外部接入</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="createdDate" label="创建日期">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiryDate" label="到期日期">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="产品描述">
            <Input.TextArea rows={3} placeholder="请输入产品描述" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AssetProductDetail
